import { useEffect, useRef, useState } from "react";

//윈터스쿨 셀프 결제 — 디자인(결제링크 사전예약.dc.html)의 동작 로직 포팅: 키보드·히스토리(이탈 방지/입금 대기 뒤로가기)·첫 진입 연출

const reducedMotion = () => { try { return window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches; } catch (e) { return false; } };

//첫 진입 연출: 세션당 1회, 1.05초 뒤 해제. 동작 줄이기 사용자는 생략, ?intro=1 이면 항상 재생(검수용)
export const useIntroOnce = () => {
    const [intro, setIntro] = useState(() => {
        try {
            const force = new URLSearchParams(window.location.search).get("intro") === "1";
            const skip = !force && (sessionStorage.getItem("sunbae_pre_intro") === "1" || reducedMotion());
            sessionStorage.setItem("sunbae_pre_intro", "1");
            return !skip;
        } catch (e) { return false; }
    });
    useEffect(() => {
        if (!intro) return;
        const t = setTimeout(() => setIntro(false), 1050);
        return () => clearTimeout(t);
    }, [intro]);
    return intro;
};

//요소가 보이는 영역(상단 sticky 타이머 아래 ~ 하단 고정 결제바 위) 밖이면 스크롤로 드러냄. center=true 면 세로 가운데로(키보드), 아니면 최소한만 움직임(달력·목록)
export const revealEl = (target: HTMLElement | null, center: boolean) => {
    if (!target || !target.isConnected) return;
    const vv = (window as any).visualViewport;
    const vTop = vv ? vv.offsetTop : 0, vH = vv ? vv.height : window.innerHeight;
    let sc: HTMLElement | null = target.parentElement;
    while (sc && sc !== document.body) { const o = getComputedStyle(sc).overflowY; if ((o === "auto" || o === "scroll") && sc.scrollHeight > sc.clientHeight) break; sc = sc.parentElement; }
    if (sc === document.body) sc = null;
    const box = sc ? sc.getBoundingClientRect() : { top: vTop, bottom: vTop + vH };
    let top = Math.max(box.top, vTop), bottom = Math.min(box.bottom, vTop + vH);
    const tm = document.querySelector("[data-pay] [data-pc-timer]") as HTMLElement | null;
    if (tm) { const p = getComputedStyle(tm).position, r = tm.getBoundingClientRect(); if ((p === "sticky" || p === "fixed") && r.top <= top + 4 && r.bottom > top) top = r.bottom; }
    //하단 고정 결제바가 떠 있으면 그 위까지만 보이는 영역
    const bar = document.querySelector("[data-pay] [data-pc-bar]") as HTMLElement | null;
    if (bar) { const p = getComputedStyle(bar).position, r = bar.getBoundingClientRect(); if ((p === "sticky" || p === "fixed") && r.bottom >= bottom - 4 && r.top < bottom) bottom = r.top; }
    top += 12; bottom -= 16;
    const r = target.getBoundingClientRect();
    if (r.top >= top && r.bottom <= bottom) return;
    let dy: number;
    if (center) dy = r.top - (top + Math.max(0, (bottom - top - r.height) / 2));
    else if (r.bottom > bottom) dy = Math.min(r.bottom - bottom, r.top - top); // 아래가 잘리면 아래쪽을 맞추되, 위쪽이 가려질 만큼은 안 올림
    else dy = r.top - top;
    if (sc) sc.scrollBy({ top: dy, behavior: "smooth" }); else window.scrollBy({ top: dy, behavior: "smooth" });
};

//모바일 키보드 — 입력칸이 키보드·하단 고정 결제바·상단 타이머에 가리지 않게 보이는 영역 가운데로 올리고, 열려 있는 동안 루트에 data-kb="1"
//안드로이드 '키보드 내리기'는 포커스를 둔 채 키보드만 닫히므로 visualViewport 높이 복구로도 data-kb 를 푼다
export const useKeyboardFit = (rootRef: React.RefObject<HTMLElement>) => {
    useEffect(() => {
        const isField = (t: any): t is HTMLElement => !!t && typeof t.matches === "function" && t.matches("[data-pay] input, [data-pay] textarea") && !["checkbox", "radio", "button"].includes(t.type);
        let el: HTMLElement | null = null;
        let t1: any, t2: any, tOut: any;
        const fit = (target: HTMLElement | null) => revealEl(target, true);
        const onIn = (e: FocusEvent) => {
            if (!isField(e.target)) return;
            clearTimeout(tOut); el = e.target as HTMLElement;
            if (rootRef.current) rootRef.current.setAttribute("data-kb", "1");
            clearTimeout(t1); clearTimeout(t2);
            t1 = setTimeout(() => fit(el), 320);
            t2 = setTimeout(() => fit(el), 650);
        };
        const onOut = (e: FocusEvent) => {
            if (!isField(e.target)) return;
            tOut = setTimeout(() => { if (!isField(document.activeElement)) { el = null; if (rootRef.current) rootRef.current.removeAttribute("data-kb"); } }, 120);
        };
        const onVV = () => {
            const vv = (window as any).visualViewport;
            const keyboardOpen = !!vv && vv.height < window.innerHeight - 100;
            if (el && document.activeElement === el) {
                if (!keyboardOpen) { if (rootRef.current) rootRef.current.removeAttribute("data-kb"); return; }
                if (rootRef.current) rootRef.current.setAttribute("data-kb", "1");
                fit(el);
            }
        };
        document.addEventListener("focusin", onIn);
        document.addEventListener("focusout", onOut);
        const vv = (window as any).visualViewport;
        if (vv) vv.addEventListener("resize", onVV);
        return () => {
            document.removeEventListener("focusin", onIn);
            document.removeEventListener("focusout", onOut);
            if (vv) vv.removeEventListener("resize", onVV);
            clearTimeout(t1); clearTimeout(t2); clearTimeout(tOut);
        };
    }, [rootRef]);
};

export interface NavGuardArgs {
    stage: "pay" | "transfer" | "waiting" | "done";
    onBackToPay: () => void;          // 입금 대기 → 결제 화면 (기기 뒤로가기)
}

//히스토리 — 입금 대기 진입 시 한 칸 쌓아 뒤로가기로 결제 화면 복귀, 제출 후(확인 대기)엔 막음. (이탈 방지 시트는 오류 소지가 있어 제거함)
export const useNavGuards = ({ stage, onBackToPay }: NavGuardArgs) => {
    const ref = useRef({ stage, onBackToPay, pushedT: false, popping: false });
    ref.current.stage = stage; ref.current.onBackToPay = onBackToPay;

    useEffect(() => {
        const onPop = () => {
            const r = ref.current;
            //제출 후(확인 대기)엔 뒤로가기로 결제 화면 복귀 막음
            if (r.pushedT && r.stage === "waiting") { try { window.history.pushState({ dcTransferDone: 1 }, ""); } catch (e) {} return; }
            if (r.pushedT && r.stage === "transfer") { r.pushedT = false; r.popping = true; r.onBackToPay(); try { window.scrollTo(0, 0); } catch (e) {} }
        };
        window.addEventListener("popstate", onPop);
        return () => { window.removeEventListener("popstate", onPop); };
    }, []);

    //입금 대기 진입/이탈에 따라 히스토리 한 칸
    const prevStage = useRef(stage);
    useEffect(() => {
        const r = ref.current;
        const was = prevStage.current; prevStage.current = stage;
        if (stage === "transfer" && was !== "transfer") { try { window.history.pushState({ dcTransferDone: 1 }, ""); r.pushedT = true; } catch (e) {} }
        if (stage === "pay" && was === "transfer" && r.pushedT && !r.popping) { r.pushedT = false; try { window.history.back(); } catch (e) {} }
        r.popping = false;
    }, [stage]);
};
